import { useEffect, useRef, useState } from 'react';
import { addThemedTileLayer, loadLeaflet, locateUser } from './leaflet';
import { haptic } from './telegram';

interface Props {
  /** Откуда начать показ. Пусто — середина европейской части. */
  start?: { lat: number; lng: number } | null;
  onPick: (p: { lat: number; lng: number }) => void;
}

const DEFAULT_CENTER: [number, number] = [55.75, 44.0];

/**
 * Выбор точки пальцем по карте.
 *
 * Нужен там, где названия нет или человек его не знает: карьер,
 * элеватор, склад на отшибе. Метка ставится по нажатию и перетаскивается —
 * промахнуться на пару километров проще, чем кажется, а повторное
 * нажатие в нужное место мельче метки не всегда попадает.
 */
export default function MapPointPicker({ start, onPick }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);
  const marker = useRef<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;

    loadLeaflet()
      .then(() => {
        if (cancelled || !box.current || map.current) return;
        const L = window.L;

        map.current = L.map(box.current, { zoomControl: true });
        addThemedTileLayer(map.current);
        map.current.setView(start ? [start.lat, start.lng] : DEFAULT_CENTER, start ? 11 : 4);

        if (start) place(start.lat, start.lng);
        map.current.on('click', (e: any) => {
          haptic('light');
          place(e.latlng.lat, e.latlng.lng);
          onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
        });

        // Карта живёт внутри модального окна и не знает своих размеров,
        // пока его не показали. Без этого виден один серый квадрат.
        setTimeout(() => map.current?.invalidateSize(), 80);

        function place(lat: number, lng: number) {
          if (marker.current) {
            marker.current.setLatLng([lat, lng]);
            return;
          }
          marker.current = L.marker([lat, lng], { draggable: true }).addTo(map.current);
          marker.current.on('dragend', () => {
            const p = marker.current.getLatLng();
            haptic('light');
            onPick({ lat: p.lat, lng: p.lng });
          });
        }
      })
      .catch((e: Error) => !cancelled && setError(e.message));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      map.current?.remove();
      map.current = null;
    },
    [],
  );

  function findMe() {
    setBusy(true);
    locateUser()
      .then(({ lat, lng }) => {
        map.current?.setView([lat, lng], 12);
        // Своё местоположение — это подсказка, куда смотреть, а не
        // готовый выбор: груз почти никогда не лежит ровно под ногами.
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy(false));
  }

  if (error) return <div className="empty">{error}</div>;

  return (
    <>
      <div className="meta" style={{ marginBottom: 'var(--s2)' }}>
        Нажмите на карту — метку можно перетащить.
      </div>
      <div className="map-wrap">
        <div ref={box} className="trackmap" style={{ height: 300 }} />
        <button
          type="button"
          className="icon-btn map-locate-btn"
          onClick={findMe}
          disabled={busy}
          title="Показать, где я"
          aria-label="Показать, где я"
        >
          {busy ? '…' : '◎'}
        </button>
      </div>
    </>
  );
}
